import { pdf } from "@react-pdf/renderer"
import { format } from "date-fns"
import { ReactElement, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks"
import { formatDelay, PromiseStatuses } from "../../lib/types"
import { colors } from "../../ui/color"
import { AirplaneIcon } from "../../ui/icons/airplane"
import { AirplaneFullIcon } from "../../ui/icons/airplaneFull"
import { ArrowLeftIcon } from "../../ui/icons/arrowLeftIcon"
import { HashIcon } from "../../ui/icons/hashIcon"
import { InfoIcon } from "../../ui/icons/infoIcon"
import { ReportIcon } from "../../ui/icons/reportIcon"
import { RouteIcon } from "../../ui/icons/routeIcon"
import { Layout } from "../../ui/layout"
import Button from "../../ui/molecoles/button"
import Pills from "../../ui/molecoles/pills"
import { Spinner } from "../../ui/molecoles/spinner"
import { ErrorPopup } from "../../ui/organisms/errorPopup"
import { HorizontalTab } from "../../ui/organisms/horizontalTab"
import { MenuItems } from "../../ui/organisms/navbar/dto"
import { PDF } from "../../ui/organisms/pdf"
import { SuccessPopup } from "../../ui/organisms/successPopup"
import { Tab } from "../../ui/organisms/tab"
import { AirportTraffic } from "./airport"
import { FlightAnalysis } from "./flightAnalysis"
import { HistoryFlight } from "./flightHistory"
import { findByIdReport, setRefreshReportStatus, setSelectedTab, setUpdateReportStatus, updateReport } from "./slice"
import { WarningsAndAlerts } from "./waringsAndAlerts"
import { Weather } from "./weather"

export function OperationReport() {
    const navigation = useNavigate()
    const dispatch = useAppDispatch()

    const reportState = useAppSelector(state => state.report)

    const [status, setStatus] = useState<PromiseStatuses>("idle")

    useEffect(() => {
        dispatch(findByIdReport(reportState.reportId))
    }, [])

    useEffect(() => {
        if (reportState.updateReportStatus === "successfully") {
            dispatch(findByIdReport(reportState.reportId))
        }
    }, [reportState.updateReportStatus])

    const viewComponentMap = new Map<number, ReactElement>([
        [0, <HistoryFlight />],
        [1, <AirportTraffic />],
        [2, <Weather />],
        [3, <WarningsAndAlerts />],
        [4, <FlightAnalysis />]
    ])

    if (reportState.findByIdStatus === "loading") {
        return (
            <div className={"flex justify-center items-center flex-grow"}>
                <Spinner />
            </div>
        )
    }

    return (
        <Layout
            menuItem={MenuItems.REPORT}
            headingLabel={
                <div className="flex flex-row items-center gap-2">
                    {"Report #" + reportState.findByIdResponse?.flight_info.flight.ident_iata}
                </div>
            }
            headingButtons={
                [
                    <>
                        <Button
                            label="Back to list"
                            size={"md"}
                            iconPosition={"left"}
                            variant={"outline"}
                            color={"gray"}
                            onClick={() => navigation("/report")}
                            icon={<ArrowLeftIcon size={24} color={colors.neutral[500]} />}
                        />
                        <Button
                            size={"md"}
                            iconPosition={"off"}
                            variant={"outline"}
                            color={"blue"}
                            label={reportState.findByIdResponse?.active_notam ? "Disable Notam" : "Enable Notam"}
                            onClick={() => {
                                if (reportState.selectedTab === 3)
                                    dispatch(setSelectedTab(0))
                                if (reportState.findByIdResponse?.active_notam) {
                                    dispatch(updateReport({ id: reportState.findByIdResponse?.id!, request: { ...reportState.updateReportRequest, active_notam: false } }))
                                } else {
                                    dispatch(updateReport({ id: reportState.findByIdResponse?.id!, request: { ...reportState.updateReportRequest, active_notam: true } }))
                                }
                            }}
                        />
                        {
                            status === "loading" ?
                                <Spinner size={35} />
                                :
                                <Button
                                    label="Download PDF"
                                    size={"md"}
                                    iconPosition={"off"}
                                    variant={"solid"}
                                    color={"blue"}
                                    onClick={() => {
                                        setStatus("loading");
                                        setTimeout(async () => {
                                            const flightCode = reportState.findByIdResponse?.flight_info.flight.ident_iata
                                            const date = reportState.findByIdResponse && format(reportState.findByIdResponse?.flight_info.flight.departure.flight_gate_times.scheduled_out, "dd-MM-yyyy")
                                            const blob = await pdf(<PDF report={reportState.findByIdResponse!} />).toBlob();

                                            const url = URL.createObjectURL(blob);
                                            const link = document.createElement("a");
                                            link.href = url;
                                            link.download = `Report-${flightCode}-${date}.pdf`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            URL.revokeObjectURL(url);

                                            setStatus("idle");
                                        }, 0);
                                    }}
                                />
                        }
                        {
                            reportState.selectedTab === 4 &&
                            <Button
                                size={"md"}
                                iconPosition={"off"}
                                variant={"solid"}
                                color={"blue"}
                                label="Update"
                                onClick={() => dispatch(updateReport({ id: reportState.findByIdResponse?.id!, request: reportState.updateReportRequest }))}
                            />
                        }
                    </>
                ]}
            breadcrumbItems={['Report', 'Dettaglio']}
            breadcrumbIcon={< ReportIcon size={20} color={colors.brandSecondary[500]} />}
        >
            <SuccessPopup
                active={reportState.updateReportStatus === "successfully"}
                close={() => dispatch(setUpdateReportStatus("idle"))}
                message="Report modificato con successo"
            />
            <ErrorPopup
                active={reportState.updateReportStatus === "failed"}
                close={() => dispatch(setUpdateReportStatus("idle"))}
                message="Errore nella modifica del report"
            />
            <SuccessPopup
                active={reportState.refreshReportStatus === "successfully"}
                close={() => dispatch(setRefreshReportStatus("idle"))}
                message="Report generato con successo"
            />
            <ErrorPopup
                active={reportState.refreshReportStatus === "failed"}
                close={() => dispatch(setRefreshReportStatus("idle"))}
                message="Errore nella generazione del report"
            />
            <div className="w-full flex flex-row gap-2 items-center">
                <div className="w-[60%] p-4 bg-pas-200 rounded-[20px] flex flex-col gap-7 h-[160px]">
                    <div className="flex flex-row gap-2 items-center">
                        <RouteIcon size={24} color={colors.pas[800]} />
                        <span className="text-[16px] font-bold text-pas-800 leading-6">
                            Flight route
                        </span>
                    </div>
                    <div className="flex flex-row items-center justify-around w-full">
                        <div className="flex flex-col gap-1">
                            <span className="text-[24px] font-semibold text-pas-700">
                                {reportState.findByIdResponse?.flight_info.flight.departure.airport.code_iata}
                            </span>
                            <span className="text-text-sm font-normal text-pas-700">
                                {reportState.findByIdResponse?.flight_info.flight.departure.airport.city}
                            </span>
                        </div>
                        <div className="flex items-center justify-center w-[50%]">
                            <div className="w-[10px] h-[10px] rounded-full bg-pas-500" />
                            <div className="relative flex items-center w-full h-[24px]">
                                <div className="absolute w-full h-[1px] border-t border-dashed border-pas-500" />
                                <div className="z-10 flex items-center justify-center w-full">
                                    <AirplaneFullIcon size={24} color={colors.pas[500]} />
                                </div>
                            </div>
                            <div className="w-[10px] h-[10px] rounded-full bg-pas-500" />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-[24px] font-semibold text-pas-700">
                                {reportState.findByIdResponse?.flight_info.flight.arrival.airport.code_iata}
                            </span>
                            <span className="text-text-sm font-normal text-pas-700">
                                {reportState.findByIdResponse?.flight_info.flight.arrival.airport.city}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="w-[40%] p-4 bg-pas-200 rounded-[20px] flex flex-col gap-7 h-[160px]">
                    <div className="flex flex-row gap-2 items-center">
                        <HashIcon size={24} color={colors.pas[800]} />
                        <span className="text-[16px] font-bold text-pas-800 leading-6">
                            Flight info
                        </span>
                    </div>
                    <div className="flex flex-col items-start gap-1 w-full">
                        <span className="text-[24px] font-semibold text-pas-700">
                            {reportState.findByIdResponse?.flight_info.flight.ident_iata}
                        </span>
                        <span className="text-text-sm font-normal text-pas-700 truncate w-full">
                            {"Airline name: " + reportState.findByIdResponse?.flight_info.flight.airline_name}
                        </span>
                        <span className="text-text-sm font-normal text-pas-700 truncate w-full">
                            {"Operated by: " + reportState.findByIdResponse?.flight_info.flight.operated_by}
                        </span>
                    </div>
                </div>
                <div className="w-[50%] p-4 bg-pas-200 rounded-[20px] flex flex-col gap-7 h-[160px]">
                    <div className="flex flex-row gap-2 items-center">
                        <AirplaneIcon size={24} color={colors.pas[800]} />
                        <span className="text-[16px] font-bold text-pas-800 leading-6">
                            Aircraft info
                        </span>
                    </div>
                    <div className="flex flex-col items-start gap-2">
                        <span className="text-[24px] font-semibold text-pas-700">
                            {
                                reportState.findByIdResponse?.flight_info.flight.aircraft_name.length! > 25
                                    ? reportState.findByIdResponse?.flight_info.flight.aircraft_name.substring(0, 25).concat("...")
                                    : reportState.findByIdResponse?.flight_info.flight.aircraft_name
                            }
                        </span>
                        <span className="text-text-sm font-normal text-pas-700">
                            {"Tail number: " + reportState.findByIdResponse?.flight_info.flight.arrival.tail_number.tail_number}
                        </span>
                        {/* <span className="text-text-sm font-normal text-pas-700">
                            {"Operated by " + reportState.findByIdResponse?.flight_info.flight.operated_by}
                        </span> */}
                    </div>
                </div>
                <div className="w-[20%] p-4 bg-pas-200 rounded-[20px] flex flex-col gap-7 h-[160px]">
                    <div className="flex flex-row gap-2 items-center">
                        <InfoIcon size={24} color={colors.pas[800]} />
                        <span className="text-[16px] font-bold text-pas-800 leading-6">
                            Status
                        </span>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                        <span className="text-[24px] font-semibold text-pas-700">
                            {
                                reportState.findByIdResponse?.flight_info.flight.history.diverted ?
                                    <Pills
                                        size={"xl"}
                                        label={"Diverted"}
                                        outline={false}
                                        emphasis={true}
                                        color={"red"}
                                    /> :
                                    reportState.findByIdResponse?.flight_info.flight.history.cancelled ?
                                        <Pills
                                            size={"xl"}
                                            label={"Canceled"}
                                            outline={false}
                                            emphasis={true}
                                            color={"red"}
                                        /> :
                                        reportState.findByIdResponse?.flight_info.flight.history.blocked ?
                                            <Pills
                                                size={"xl"}
                                                label={"Blocked"}
                                                outline={false}
                                                emphasis={true}
                                                color={"red"}
                                            /> :
                                            formatDelay(reportState.findByIdResponse?.flight_info.flight.arrival.flight_gate_times.arrival_delay!)?.includes("early") ?
                                                <Pills
                                                    size={"xl"}
                                                    label={"Landed"}
                                                    outline={false}
                                                    emphasis={true}
                                                    color={"green"}
                                                /> :
                                                <div className="flex flex-col items-start gap-[12px]">
                                                    <Pills
                                                        size={"xl"}
                                                        label={"Delayed"}
                                                        outline={false}
                                                        emphasis={true}
                                                        color={"orange"}
                                                    />
                                                    <span className="text-text-sm font-normal text-pas-700">
                                                        {
                                                            (reportState.findByIdResponse?.flight_info.flight.arrival.flight_gate_times.arrival_delay === "" || reportState.findByIdResponse?.flight_info.flight.arrival.flight_gate_times.arrival_delay === "0s") ?
                                                                "-" : formatDelay(reportState.findByIdResponse?.flight_info.flight.arrival.flight_gate_times.arrival_delay!)
                                                        }
                                                    </span>
                                                </div>
                            }
                        </span>
                    </div>
                </div>
            </div>
            <HorizontalTab>
                <Tab
                    label={"Flight History"}
                    type="primary"
                    selectedPrimary={reportState.selectedTab === 0}
                    onClick={() => dispatch(setSelectedTab(0))}
                />
                <Tab
                    label={"Airports Traffic"}
                    selectedPrimary={reportState.selectedTab === 1}
                    onClick={() => dispatch(setSelectedTab(1))}
                    type="primary"
                />
                <Tab
                    label={"Weather"}
                    selectedPrimary={reportState.selectedTab === 2}
                    onClick={() => dispatch(setSelectedTab(2))}
                    type="primary"
                />
                <Tab
                    label={"Notam"}
                    selectedPrimary={reportState.selectedTab === 3}
                    onClick={() => dispatch(setSelectedTab(3))}
                    type="primary"
                    disabled={!reportState.findByIdResponse?.active_notam}
                />
                <Tab
                    label={"Final Analysis"}
                    selectedPrimary={reportState.selectedTab === 4}
                    onClick={() => dispatch(setSelectedTab(4))}
                    type="primary"
                />
            </HorizontalTab>
            {viewComponentMap.get(reportState.selectedTab)}
        </Layout >
    )
}